import { MeetingContainerAPI, MeetingSessionOvenMediaReceiverManagerBaseData, useMeetingSelector } from "@kalyzee/kast-web-sdk";
import { OvenMediaReceiverWebRTCSession, OvenMediaReceiverWebRTCSubscription } from "@kalyzee/kast-web-sdk/webrtc";
import { MeetingSession, MeetingSessionMedia } from "@kalyzee/kast-web-sdk/websocket";
import { useMemo, useRef } from "react";
import { useAppStoreSelector } from "../contexts/appStore/selectors";

export const useVideoSubscriptions = () => {
  const ref = useRef<{ [id: string]: OvenMediaReceiverWebRTCSubscription }>({});

  const update = (subscriptions: {
    session: OvenMediaReceiverWebRTCSession,
    active: boolean,
  }[]) => {
    subscriptions.forEach(({ session, active }) => {
      const existing = ref.current[session.getId()];
      if (active) {
        if (!existing) {
          ref.current[session.getId()] = session.subscribe({ video: true, audio: false });
        }
      } else {
        if (existing) {
          existing.unsubscribe();
          delete ref.current[session.getId()];
        }
      }
    });
  };

  const reset = () => {
    Object.values(ref.current).forEach(subscription => subscription.unsubscribe());
    ref.current = {};
  };

  return { update, reset };
};

export type LocalOrRemoteMedia = {
  session: MeetingSession;
  mediaSession: MeetingSessionMedia;
} & (
  | { type: "local"; stream: MediaStream }
  | { type: "remote"; videoSession: OvenMediaReceiverWebRTCSession<MeetingSessionOvenMediaReceiverManagerBaseData> }
);

export const useLocalOrRemoteMedia = (
  meetingAPI?: MeetingContainerAPI,
  filter?: (session: MeetingSession, media: MeetingSessionMedia) => boolean,
) => {
  const [session] = useMeetingSelector(meetingAPI, "session");
  const [sessions] = useMeetingSelector(meetingAPI, "sessions");
  const [receptionSessions] = useMeetingSelector(meetingAPI, "receptionSessions");

  const selfStreams = useAppStoreSelector(state => state.selfStreams);

  return useMemo(
    () => {
      if (!filter) return [];

      const foundMedias = new Set<string>();
      const inputs: LocalOrRemoteMedia[] = [];

      if (session) {
        session.medias.forEach((media) => {
          if (!filter(session, media)) return;
          const localMedia = media.name ? selfStreams[media.name] : undefined;
          if (localMedia) {
            inputs.push({ type: "local", session, mediaSession: media, stream: localMedia });
            foundMedias.add(media.id);
          }
        });
      }

      receptionSessions?.forEach((receptionSession) => {
        if (!filter(receptionSession.data.session, receptionSession.data.mediaSession)) return;
        if (foundMedias.has(receptionSession.data.mediaSession.id)) return; // we alreay have the local version
        inputs.push({
          type: "remote",
          mediaSession: receptionSession.data.mediaSession,
          session: receptionSession.data.session,
          videoSession: receptionSession,
        });
        foundMedias.add(receptionSession.data.mediaSession.id);
      });

      return inputs;
    },
    // /!\ leave sessions in the dependency, because the receptionsSessions array is not rebuilt on each Meeting session update by the SDK (bug)
    [session, sessions, selfStreams, receptionSessions, filter],
  );
}